import { X } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { lockBodyScroll } from "@/lib/scroll-lock";
import { useT } from "@/lib/i18n";

const modalStack: HTMLDivElement[] = [];
const previousInert = new Map<HTMLElement, boolean>();

function syncModalLayers() {
  const top = modalStack.at(-1);
  if (!top) {
    previousInert.forEach((inert, element) => { element.inert = inert; });
    previousInert.clear();
    return;
  }
  for (const element of document.body.children) {
    if (!(element instanceof HTMLElement)) continue;
    if (!previousInert.has(element)) previousInert.set(element, element.inert);
    element.inert = !element.contains(top);
  }
}

type ModalProps = {
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** When false, Escape and backdrop clicks do nothing; only the explicit close button (or an action) dismisses it. */
  dismissible?: boolean;
  closeDisabled?: boolean;
  wide?: boolean;
  compact?: boolean;
  icon?: ReactNode;
};

/** Accessible modal: dialog semantics, Escape/backdrop dismissal, focus moved in and restored, page scroll locked. */
export function Modal({ onClose, title, children, dismissible = true, closeDisabled = false, wide = false, compact = false, icon }: ModalProps) {
  const t = useT();
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const card = cardRef.current;
    if (card) modalStack.push(card);
    syncModalLayers();
    const unlock = lockBodyScroll();
    card?.focus();

    const onKeydown = (event: KeyboardEvent) => {
      if (modalStack.at(-1) !== card) return;
      if (event.key === "Escape" && dismissible) { event.preventDefault(); event.stopImmediatePropagation(); onCloseRef.current(); return; }
      if (event.key !== "Tab" || !card) return;
      const focusable = Array.from(card.querySelectorAll<HTMLElement>("a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])")).filter((element) => !element.hidden && !element.closest("[hidden], [inert]") && getComputedStyle(element).display !== "none" && getComputedStyle(element).visibility !== "hidden");
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === card)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === card || !card.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    };
    const keepFocus = (event: FocusEvent) => {
      if (modalStack.at(-1) === card && event.target instanceof Node && !card?.contains(event.target)) card?.focus();
    };
    document.addEventListener("focusin", keepFocus);
    document.addEventListener("keydown", onKeydown);
    return () => {
      document.removeEventListener("keydown", onKeydown);
      document.removeEventListener("focusin", keepFocus);
      const index = card ? modalStack.indexOf(card) : -1;
      if (index >= 0) modalStack.splice(index, 1);
      syncModalLayers();
      unlock();
      if (previous?.isConnected) previous.focus();
    };
  }, [dismissible]);

  return createPortal(
    <div className="modal-backdrop" onMouseDown={(event) => { if (dismissible && event.target === event.currentTarget) onClose(); }}>
      <div ref={cardRef} className={["modal-card", wide ? "modal-card--wide" : "", compact ? "modal-card--compact" : ""].filter(Boolean).join(" ")} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <button type="button" className="modal-close" aria-label={t("common.close")} disabled={closeDisabled} onClick={onClose}><X size={18} aria-hidden="true" /></button>
        <div className="modal-head">
          {icon ? <span className="modal-icon" aria-hidden="true">{icon}</span> : null}
          <h2 id={titleId}>{title}</h2>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}
